import React, { useEffect, useMemo, useState } from 'react';
import { FiRefreshCcw, FiUsers } from 'react-icons/fi';

import { getMentorDashboard } from '../../../services/mentorService';
import LoadingSpinner from '../../LoadingSpinner';

// Cohort-flavored panels live next to this shell
import CohortKpiBar from './CohortKpiBar';
import MenteeLeaderboard from './MenteeLeaderboard';
import NeedsAttentionPanel from './NeedsAttentionPanel';
import MenteeOutcomeMatrix from './MenteeOutcomeMatrix';
import CohortResumeHealth from './CohortResumeHealth';
import MentorInviteFooter from './MentorInviteFooter';

// Reused directly from the user Statistics tab — the data contracts for
// these panels (highlights, sankey, velocity, heatmap, journey) are already
// cohort-agnostic so the backend builders can feed them with merged data.
import WeeklyDigest from '../../analytics/WeeklyDigest';
import AnalyticsSankey from '../../analytics/AnalyticsSankey';
import VelocityCards from '../../analytics/VelocityCards';
import ActivityHeatmap from '../../analytics/ActivityHeatmap';
import JourneyTimeline from '../../analytics/JourneyTimeline';

/**
 * Mentor analytics dashboard. Layout (desktop, lg+) mirrors the user-facing
 * Statistics tab but reframes every panel as "the cohort" instead of "me".
 *
 *   +----------------- Weekly Coaching Digest (hero) -----------------+
 *   +--------------------- Cohort KPI strip --------------------------+
 *   +------- Pipeline Sankey (8) ---+--- Velocity sparklines (4) -----+
 *   +-- Mentee Leaderboard (7) -----+------ Needs Attention (5) ------+
 *   +-- Outcome Matrix (5) ---------+--- Cohort Resume Health (7) ----+
 *   +--------------------- Activity Heatmap (12) ---------------------+
 *   +------------------ Mentee Highlights Timeline -------------------+
 *   +--------------------- Invite & quick actions --------------------+
 *
 * The grid is constrained to ~1400px and centered so it doesn't stretch on
 * ultra-wide displays — same as the user dashboard.
 */
const MentorAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await getMentorDashboard();
      setPayload(res.data || {});
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load mentor dashboard.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData(false);
  }, []);

  const data = useMemo(() => payload || {}, [payload]);

  if (loading) {
    return <LoadingSpinner message="Loading mentor dashboard…" />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center max-w-xl mx-auto my-6">
        <p className="text-red-700 font-semibold mb-1">Couldn&apos;t load mentor dashboard</p>
        <p className="text-red-600 text-sm mb-4">{error}</p>
        <button
          type="button"
          onClick={() => fetchData(false)}
          className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // No mentees yet — surface the invite block as the only action.
  if (!data.hasData) {
    return (
      <div className="max-w-[1400px] mx-auto">
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center max-w-xl mx-auto mb-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-50 mb-3">
            <FiUsers className="h-5 w-5 text-primary-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">Your mentor dashboard is ready</h3>
          <p className="text-sm text-gray-500 mb-4">
            No mentees are linked to you yet. Share your mentor code below to start
            growing your cohort — analytics will appear here as soon as your first
            mentee signs up and starts applying.
          </p>
          <button
            type="button"
            onClick={() => fetchData(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            <FiRefreshCcw className="mr-2 h-4 w-4" /> Refresh
          </button>
        </div>
        <MentorInviteFooter
          invite={data.invite}
          onCodeUpdated={() => fetchData(true)}
        />
      </div>
    );
  }

  const heatmap = data.heatmap || { data: [], dayOfWeekDistribution: [] };
  const journey = data.journey || {};

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-base font-bold text-gray-900 tracking-tight">Mentor Dashboard</h1>
          <p className="text-xs text-gray-500">
            Cohort coaching analytics across {data.totalMentees ?? 0} mentee{(data.totalMentees ?? 0) === 1 ? '' : 's'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="inline-flex items-center px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-md bg-white hover:bg-gray-50 disabled:opacity-60"
        >
          <FiRefreshCcw className={`mr-1.5 h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Row 1 — Hero digest */}
        <div className="col-span-12">
          <WeeklyDigest digest={data.digest} />
        </div>

        {/* Row 2 — Cohort KPI strip */}
        <div className="col-span-12">
          <CohortKpiBar data={data.kpis} />
        </div>

        {/* Row 3 — Pipeline Sankey (left, wide) + Velocity (right, narrow stacked) */}
        <div className="col-span-12 lg:col-span-8">
          <AnalyticsSankey data={data.sankey} />
        </div>
        <div className="col-span-12 lg:col-span-4">
          <VelocityCards data={data.velocity} stacked />
        </div>

        {/* Row 4 — Mentee Leaderboard (wide) + Needs Attention */}
        <div className="col-span-12 lg:col-span-7">
          <MenteeLeaderboard rows={data.leaderboard} />
        </div>
        <div className="col-span-12 lg:col-span-5">
          <NeedsAttentionPanel items={data.attention} />
        </div>

        {/* Row 5 — Outcome Matrix (compact) + Cohort Resume Health (wide) */}
        <div className="col-span-12 lg:col-span-5">
          <MenteeOutcomeMatrix items={data.outcomeMatrix} />
        </div>
        <div className="col-span-12 lg:col-span-7">
          <CohortResumeHealth data={data.resumeHealth} />
        </div>

        {/* Row 6 — Heatmap (full) */}
        <div className="col-span-12">
          <ActivityHeatmap
            heatmapData={heatmap.data}
            dayOfWeekDistribution={heatmap.dayOfWeekDistribution}
          />
        </div>

        {/* Row 7 — Mentee Highlights Timeline (full) */}
        <div className="col-span-12">
          <JourneyTimeline events={journey.events} storyArcs={journey.storyArcs} />
        </div>

        {/* Footer — Invite + quick actions */}
        <div className="col-span-12">
          <MentorInviteFooter
            invite={data.invite}
            onCodeUpdated={() => fetchData(true)}
          />
        </div>
      </div>
    </div>
  );
};

export default MentorAnalyticsDashboard;
